import type {
  ApprovalDecision,
  ApprovalDetail,
  ApprovalHistory,
  ApprovalKind,
  PoolCheck,
  PoolLedger,
  RoleKey,
  SaveApprovalReview,
} from '@myrewards/contracts';
import {
  daysBetween,
  formatCount,
  formatDate,
  formatMoney,
  formatShortDate,
  organizationDate,
  plural,
  scoped,
  type ScopedLabel,
} from '../../ui';
import { firstName } from '../../acting-for/words';
import { amountWords, TYPE_LABEL } from '../labels';

type Line = ApprovalDetail['lines'][number];
type Units = { cashCents: number; options: number };
type TopUp = NonNullable<Extract<ApprovalDecision, { action: 'approve' }>['topUps']>[number];

/**
 * The one "acting" rule (APP-148): what a Stock Admin may do on this award for someone else, when
 * they don't decide it in their own right; null otherwise. The hero, the buttons and the list
 * all read it.
 */
export function actingOffer(
  detail: ApprovalDetail,
): NonNullable<ApprovalDetail['actingFor']> | null {
  return decidesOwn(detail) ? null : (detail.actingFor ?? null);
}

// --- Headings ----------------------------------------------------------------------------------------

/**
 * The hero's words for each kind of decision, including pending historical approvals (APP-121).
 */
export function approvalHeading(detail: ApprovalDetail): {
  eyebrow: string;
  title: string;
  sub: string;
} {
  const { award } = detail;
  const id = award.displayId ?? 'This award';
  const where = award.sector?.name ?? 'Executive';
  const by = award.onBehalfOf
    ? `${award.createdBy.name} for ${award.onBehalfOf.name}`
    : award.createdBy.name;
  switch (detail.kind) {
    case 'self_recognition':
      return {
        eyebrow: 'Executive · Approvals',
        title: `Approve ${id}`,
        sub: 'Any one Executive can approve this award. Any over-budget pools need a top-up in the same decision.',
      };
    case 'delegate':
      return {
        eyebrow: `${where} · Approvals`,
        title: `Approve ${id}`,
        // Not entered for the lead: someone named to start Sector bonuses here (APP-111).
        sub:
          award.onBehalfOf === null
            ? `${by} entered this ${TYPE_LABEL[award.type]}. They were named to start Sector bonuses in ${where} and aren’t a delegate, so it comes to you first.`
            : `${by} entered this ${TYPE_LABEL[award.type]}. Entries from delegates who need your OK stop here first.`,
      };
    case 'overage': {
      const over = detail.budget.filter((p) => p.status === 'over_budget');
      const pools = over.map((p) => p.pool.name).join(' and ');
      const short = over.map((p) =>
        amountWords(p.shortfall?.cashCents ?? 0, p.shortfall?.options ?? 0),
      );
      return {
        eyebrow: `Executive · Approvals · Over budget${award.sector ? ` · ${award.sector.name}` : ''}`,
        title: `Approve ${id}`,
        sub:
          over.length > 0
            ? `${by}’s ${TYPE_LABEL[award.type]}${award.program ? ` for ${award.program.name}` : ''} is ${short.join(' and ')} over the ${pools} ${over.length === 1 ? 'pool' : 'pools'}. Any one Executive can approve it with a top-up, or decline it.`
            : `A budget added since ${by} sent it now covers ${id}. It still needs an Executive’s decision.`,
      };
    }
    case 'executive':
      return {
        eyebrow: `Executive · Approvals · ${award.type === 'TAA' ? 'TAA' : 'PAA'}`,
        title: 'Record the Board’s decision',
        sub: 'The Executives, the President and the Board decided outside the app. Match the people and amounts to what they agreed, then approve. Any one Executive can.',
      };
    case 'record_decision':
      return {
        eyebrow: `${id} · Executive decision, outside the app`,
        title: 'Record what the Executives approved',
        sub: 'Update the request to what was approved and attach the proof. Then it goes to the Stock Admin team for the one check.',
      };
    case null:
      return {
        eyebrow: `${id} · ${TYPE_LABEL[award.type]}`,
        title: `${id} is decided`,
        sub: decidedSentence(detail),
      };
  }
}

/**
 * The hero's words for a Stock Admin who may do this for an Executive or a Sector Lead (APP-148,
 * Overage and LeadApprove boards): whose decision it is, and that they can do it when asked.
 */
export function actingHeading(
  detail: ApprovalDetail,
  own: { eyebrow: string; title: string; sub: string },
): { eyebrow: string; title: string; sub: string } {
  const offer = detail.actingFor;
  if (offer == null) return own;
  const sector = detail.award.sector?.name ?? null;
  if (offer.role === 'sector_lead') {
    const lead = offer.people[0]?.name ?? 'the Sector Lead';
    const first = lead.split(' ')[0] ?? lead;
    const { award } = detail;
    return {
      eyebrow: `${sector ?? 'Sector'} · approvals · waiting on ${lead}`,
      title: own.title,
      sub:
        detail.kind === 'delegate'
          ? `${award.createdBy.name} entered this ${TYPE_LABEL[award.type]} for ${lead}. A delegate’s entries wait for ${first}’s OK. When ${first} asks you to, you can decide it for ${first}.`
          : `${own.sub} When ${first} asks you to, you can do it for ${first}.`,
    };
  }
  // APP-274: an entry from a delegate who needs their Executive's approval.
  if (detail.kind === 'delegate') {
    const { award } = detail;
    const forName = award.onBehalfOf?.name ?? 'an Executive';
    const named = offer.fixed ? offer.people[0] : undefined;
    const first = named === undefined ? null : (named.name.split(' ')[0] ?? named.name);
    return {
      eyebrow: `${sector ?? 'Approvals'} · approvals · waiting on ${named?.name ?? 'the Executives'}`,
      title: own.title,
      sub:
        first === null
          ? `${award.createdBy.name} entered this ${TYPE_LABEL[award.type]} for ${forName}, who is no longer an Executive, so any one Executive can decide it. When one asks you to, you can decide it for them.`
          : `${award.createdBy.name} entered this ${TYPE_LABEL[award.type]} for ${forName}. A delegate’s entries wait for ${first}’s OK. When ${first} asks you to, you can decide it for ${first}.`,
    };
  }
  const kind =
    detail.kind === 'overage' ? 'over budget' : detail.kind === null ? null : 'Executive decision';
  return {
    eyebrow: ['Approvals', kind, sector, 'for the Executives']
      .filter((part): part is string => part !== null)
      .join(' · '),
    title: own.title,
    sub: `${own.sub} When an Executive asks you to, you can do it for them.`,
  };
}

/** "Approved by Elena Brightwater on Oct 14, 2026." or where the award went instead. */
export function decidedSentence(detail: ApprovalDetail): string {
  const { award, decision } = detail;
  if (award.ended === 'rejected') return 'It was turned down, and whoever entered it was told.';
  if (award.ended === 'cancelled') return 'It was cancelled.';
  if (award.stage === 'draft')
    return 'It went back to whoever entered it, to change and send again.';
  const who = decision.approvedBy ? ` by ${decision.approvedBy.name}` : '';
  const when = decision.approvedAt ? ` on ${formatDate(decision.approvedAt)}` : '';
  return `It was approved${who}${when}. Nothing here is waiting on you.`;
}

// --- People and amounts under review -------------------------------------------------------------

export interface LineEdit {
  included: boolean;
  /** EDB, TAA and a PAA (its award, paid in cash; APP-151): cash for the person. */
  cashCents: number | null;
  /** EDB: options for the person. */
  options: number | null;
  /** An older PAA (`paaAmount: 'total_value'`): the total value; the server works out the rest. */
  totalValueCents: number | null;
}

/**
 * APP-151: an older PAA under review, whose amount is its total value split by its saved share.
 * Every PAA since is changed by its cash; the server adds the options on top when it's saved.
 */
export function reviewsPaaValue(detail: ApprovalDetail): boolean {
  return detail.award.type === 'PAA' && detail.award.calculation.paaAmount === 'total_value';
}

/**
 * Only what the person changed, by line id, over the saved lines. A person move or any other new
 * answer from the server keeps unsaved changes; saving the review clears them.
 */
export type Edits = Readonly<Record<string, Partial<LineEdit>>>;

/** A line as the form shows it: the saved line with the person's changes over it. */
export function editOf(edits: Edits, line: Line): LineEdit {
  return {
    included: line.included,
    cashCents: line.cashCents,
    options: line.options,
    totalValueCents: line.totalValueCents,
    ...edits[line.id],
  };
}

/** Records one change, dropping it again when it matches the saved line. */
export function withEdit(edits: Edits, line: Line, change: Partial<LineEdit>): Edits {
  const merged: Partial<LineEdit> = { ...edits[line.id], ...change };
  const saved = editOf({}, line);
  const next = Object.fromEntries(
    Object.entries(merged).filter(([key, value]) => value !== saved[key as keyof LineEdit]),
  ) as Partial<LineEdit>;
  const rest = Object.fromEntries(Object.entries(edits).filter(([id]) => id !== line.id));
  return Object.keys(next).length === 0 ? rest : { ...rest, [line.id]: next };
}

function lineChanged(line: Line, edit: LineEdit): boolean {
  return (
    edit.included !== line.included ||
    edit.cashCents !== line.cashCents ||
    edit.options !== line.options ||
    edit.totalValueCents !== line.totalValueCents
  );
}

export function isDirty(detail: ApprovalDetail, edits: Edits): boolean {
  return detail.lines.some((line) => lineChanged(line, editOf(edits, line)));
}

/** Why the edits can't be saved yet (one sentence each), or nothing. */
export function editProblems(detail: ApprovalDetail, edits: Edits): string[] {
  const problems: string[] = [];
  const included = detail.lines.filter((l) => editOf(edits, l).included);
  if (included.length === 0)
    problems.push('Keep at least one person, or decline the award instead.');
  for (const line of included) {
    const edit = editOf(edits, line);
    const missing = reviewsPaaValue(detail)
      ? edit.totalValueCents === null || edit.totalValueCents <= 0
      : detail.award.type === 'TAA' || detail.award.type === 'PAA'
        ? edit.cashCents === null || edit.cashCents <= 0
        : (edit.cashCents ?? 0) <= 0 && (edit.options ?? 0) <= 0;
    if (missing) problems.push(`Enter an amount for ${line.personName}.`);
  }
  return problems;
}

/**
 * The review to save: every line exactly once with whether it's included, and only the amount
 * fields this award type takes, only where they changed (PAA cash, or an older PAA's total value;
 * EDB cash and options; TAA cash). Amounts on a removed line aren't sent.
 */
export function reviewPayload(detail: ApprovalDetail, edits: Edits): SaveApprovalReview {
  return {
    version: detail.award.version,
    lines: detail.lines.map((line) => {
      const edit = editOf(edits, line);
      const row: SaveApprovalReview['lines'][number] = { lineId: line.id, included: edit.included };
      if (!edit.included) return row;
      if (reviewsPaaValue(detail)) {
        if (edit.totalValueCents !== null && edit.totalValueCents !== line.totalValueCents)
          row.totalValueCents = edit.totalValueCents;
        return row;
      }
      if (edit.cashCents !== null && edit.cashCents !== line.cashCents)
        row.cashCents = edit.cashCents;
      // A PAA's options are worked out from its cash; a TAA has none.
      const takesOptions = detail.award.type !== 'TAA' && detail.award.type !== 'PAA';
      if (takesOptions && edit.options !== null && edit.options !== line.options)
        row.options = edit.options;
      return row;
    }),
  };
}

/** People, cash and value counted from the edits (the summary beside the form). */
export function editedTotals(
  detail: ApprovalDetail,
  edits: Edits,
): { people: number; of: number; cashCents: number; options: number; valueCents: number } {
  let people = 0;
  let cashCents = 0;
  let options = 0;
  let valueCents = 0;
  for (const line of detail.lines) {
    const edit = editOf(edits, line);
    if (!edit.included) continue;
    people += 1;
    cashCents += edit.cashCents ?? 0;
    options += edit.options ?? 0;
    valueCents += edit.totalValueCents ?? 0;
  }
  return { people, of: detail.lines.length, cashCents, options, valueCents };
}

// --- Pools ---------------------------------------------------------------------------------------------

export interface PoolRef {
  id: string;
  name: string;
}

/** Every pool the award shows, by id: from the budget check and the "Paid from" list. */
export function poolNames(detail: ApprovalDetail): Map<string, string> {
  const names = new Map<string, string>();
  for (const p of detail.award.calculation.paidFrom) names.set(p.pool.id, p.pool.name);
  for (const p of detail.budget) names.set(p.pool.id, p.pool.name);
  return names;
}

/**
 * The two pools a TAA person can be paid from: the PAA/TAA pool and the award's sector pool.
 * Built from the pools the award shows, plus the sector's own pool by its id.
 */
export function taaPoolChoices(detail: ApprovalDetail): PoolRef[] {
  // The server names both a TAA's pools, so one paying no one now is still offered (QA-048).
  const funding = detail.taaFunding;
  if (funding != null) return [funding.paaPool, funding.sectorPool];
  const names = poolNames(detail);
  const sector = detail.award.sector;
  if (
    sector &&
    ![...names.keys()].includes(sector.id) &&
    ![...names.values()].includes(sector.name)
  )
    names.set(sector.id, sector.name);
  return [...names.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => Number(a.name === sector?.name) - Number(b.name === sector?.name));
}

/** "Paid from": each pool with how many people it pays and how much (amounts, never a fraction). */
export function paidFromLines(
  detail: ApprovalDetail,
  edits: Edits = {},
): { pool: PoolRef; people: number; cashCents: number; options: number }[] {
  const names = poolNames(detail);
  const rows = new Map<
    string,
    { pool: PoolRef; people: number; cashCents: number; options: number }
  >();
  for (const line of detail.lines) {
    const edit = editOf(edits, line);
    if (!edit.included) continue;
    const row = rows.get(line.poolId) ?? {
      pool: { id: line.poolId, name: names.get(line.poolId) ?? line.poolId },
      people: 0,
      cashCents: 0,
      options: 0,
    };
    row.people += 1;
    row.cashCents += edit.cashCents ?? 0;
    row.options += edit.options ?? 0;
    rows.set(line.poolId, row);
  }
  return [...rows.values()];
}

// --- Budget and top-ups -------------------------------------------------------------------------------

/** The award's sector pool as its budget lists it, else its first pool (SL7, LeadApprove). */
export function sectorPool(detail: ApprovalDetail): ApprovalDetail['budget'][number] | undefined {
  return detail.budget.find((p) => p.pool.id === detail.award.sector?.id) ?? detail.budget[0];
}

/** What this award asks of a pool, from its saved "Paid from". */
export function demandOf(detail: ApprovalDetail, poolId: string): Units {
  const row = detail.award.calculation.paidFrom.find((p) => p.pool.id === poolId);
  return { cashCents: row?.cashCents ?? 0, options: row?.options ?? 0 };
}

export interface TopUpDraft {
  pool: PoolRef;
  cashCents: number | null;
  options: number | null;
  /** The amount that brings the pool back to zero: the default (PRD §7). */
  shortfall: Units;
  /** The pool's balance after this award, before any top-up (negative when over). */
  remainingAfter: Units;
  /** Ask for options too: the award gives options from this pool, or the pool is short of them. */
  withOptions: boolean;
}

export function overBudgetPools(detail: ApprovalDetail): PoolCheck[] {
  return detail.budget.filter((p) => p.status === 'over_budget');
}

/** One draft per over-budget pool, defaulting to the current shortfall. */
export function topUpDrafts(detail: ApprovalDetail): TopUpDraft[] {
  return overBudgetPools(detail).map((p) => {
    const suggested = detail.suggestedTopUps.find((t) => t.poolId === p.pool.id);
    const shortfall = p.shortfall ?? { cashCents: 0, options: 0 };
    const cash = suggested?.cashCents ?? shortfall.cashCents;
    const options = suggested?.options ?? shortfall.options;
    return {
      pool: p.pool,
      cashCents: cash,
      options,
      shortfall,
      remainingAfter: p.remainingAfter ?? { cashCents: 0, options: 0 },
      withOptions: options > 0 || demandOf(detail, p.pool.id).options > 0,
    };
  });
}

/**
 * The explicit top-up for every over-budget pool: any amount from zero up (PRD §7, APP-037).
 * An empty field isn't zero: it has to be filled in.
 */
export function topUpPayload(
  drafts: readonly TopUpDraft[],
): { ok: true; topUps: TopUp[] } | { ok: false; message: string } {
  const topUps: TopUp[] = [];
  for (const draft of drafts) {
    if (draft.cashCents === null || (draft.withOptions && draft.options === null))
      return {
        ok: false,
        message: `Enter the top-up for ${draft.pool.name}. Zero is allowed; it leaves the pool over budget.`,
      };
    topUps.push({
      poolId: draft.pool.id,
      cashCents: draft.cashCents,
      options: draft.withOptions ? (draft.options ?? 0) : 0,
    });
  }
  return { ok: true, topUps };
}

export function afterTopUp(draft: TopUpDraft): Units {
  return {
    cashCents: draft.remainingAfter.cashCents + (draft.cashCents ?? 0),
    options: draft.remainingAfter.options + (draft.withOptions ? (draft.options ?? 0) : 0),
  };
}

/** "Terrestrial will be at $0 for the rest of the year." and its variants. */
export function topUpSentence(draft: TopUpDraft): { text: string; tone: 'done' | 'problem' } {
  const after = afterTopUp(draft);
  const name = draft.pool.name;
  const cashShort = after.cashCents < 0;
  const optionsShort = draft.withOptions && after.options < 0;
  if (cashShort || optionsShort) {
    const short = amountWords(cashShort ? -after.cashCents : 0, optionsShort ? -after.options : 0);
    return {
      text: `${name} will stay ${short} over budget for the rest of the year.`,
      tone: 'problem',
    };
  }
  if (after.cashCents === 0 && (!draft.withOptions || after.options === 0))
    return { text: `${name} will be at $0 for the rest of the year.`, tone: 'done' };
  const left = draft.withOptions
    ? `${formatMoney(after.cashCents)} and ${plural(after.options, 'option', 'options')}`
    : formatMoney(after.cashCents);
  return { text: `${name} will have ${left} left for the rest of the year.`, tone: 'done' };
}

/** Back to zero is the default; anything else is the Executive's choice, and says so. */
export function isDefaultTopUp(draft: TopUpDraft): boolean {
  return (
    draft.cashCents === draft.shortfall.cashCents &&
    (!draft.withOptions || draft.options === draft.shortfall.options)
  );
}

// --- X3: a TAA's split between its two pools, by person (QA-048) -----------------------------------------

type Share = { num: number; den: number };

/** The split presets on X3 (the board's own control): the PAA/TAA pool's percent first. */
export const SPLIT_PRESETS: readonly number[] = [50, 70, 30];

/** The two shares are the same split (1/2 and 50/100 are). */
export function sameShare(a: Share, b: Share): boolean {
  return a.num * b.den === b.num * a.den;
}

/** The PAA/TAA pool's share in whole percent (one third shows as 33). */
export function sharePercent(share: Share): number {
  return Math.round((share.num / share.den) * 100);
}

/**
 * "70 / 30": the PAA/TAA pool's part of a TAA's cash, then the sector pool's, by person. This is
 * the pools' split on the board's control; it is never the cash/options split, which award screens
 * never show.
 */
export function splitWords(share: Share): string {
  const paa = sharePercent(share);
  return `${String(paa)} / ${String(100 - paa)}`;
}

/** The PAA/TAA pool and the sector pool a TAA draws on, in that order. */
export function taaPools(detail: ApprovalDetail): { paa: PoolRef; sector: PoolRef } | null {
  const funding = detail.taaFunding;
  if (funding != null) return { paa: funding.paaPool, sector: funding.sectorPool };
  const choices = taaPoolChoices(detail);
  const sectorId = detail.award.sector?.id;
  const sector =
    choices.find((p) => p.id === sectorId || p.name === detail.award.sector?.name) ?? null;
  const paa = choices.find((p) => p !== sector) ?? null;
  return paa === null || sector === null ? null : { paa, sector };
}

/** Who changed pool between two versions of one award, grouped by the pool they're on now. */
export function movedBetween(
  before: ApprovalDetail,
  after: ApprovalDetail,
): { pool: PoolRef; names: string[] }[] {
  const pools = taaPoolChoices(after);
  const groups = new Map<string, { pool: PoolRef; names: string[] }>();
  for (const line of after.lines) {
    const old = before.lines.find((l) => l.id === line.id);
    if (old === undefined || old.poolId === line.poolId || !line.included) continue;
    const pool = pools.find((p) => p.id === line.poolId) ?? { id: line.poolId, name: line.poolId };
    const group = groups.get(pool.id) ?? { pool, names: [] };
    group.names.push(line.personName);
    groups.set(pool.id, group);
  }
  return [...groups.values()];
}

/**
 * The pool-moves line under X3's people (QA-048): where everyone is now and the split it follows.
 * It says the app assigned them only while the assignment is still the app's: any later save
 * or move makes it the approver's, and then it states the facts.
 */
export function poolMovesWords(detail: ApprovalDetail): string | null {
  const funding = detail.taaFunding;
  const pools = taaPools(detail);
  if (funding == null || pools === null) return null;
  const live = detail.lines.filter((l) => l.included);
  const onPaa = live.filter((l) => l.poolId === pools.paa.id).length;
  const onSector = live.length - onPaa;
  const split = `${splitWords(funding.paaShare)} by person`;
  const changed = !sameShare(funding.paaShare, funding.defaultShare);
  const why = changed
    ? ` This award’s split, changed from the ${splitWords(funding.defaultShare)} default${funding.reason === null ? '' : `: ${funding.reason}`}.`
    : '';
  return funding.assignment === 'auto'
    ? `The app assigned ${plural(onPaa, 'person', 'people')} to the ${pools.paa.name} pool and ${String(onSector)} to the ${pools.sector.name} pool, to match ${split}.${why}`
    : `${plural(onPaa, 'person', 'people')} on the ${pools.paa.name} pool and ${String(onSector)} on the ${pools.sector.name} pool · split ${split}.${why}`;
}

// --- X2: the over-budget pool's ledger -----------------------------------------------------------------

export interface OverageLedgerRow {
  id: string;
  /** "Jan 2, 2026", or "Through Oct 13" for the awards so far. */
  date: string;
  entry: string;
  sub: string;
  /** Signed: what this line adds to or takes from the pool. */
  amountCents: number;
  /** Where the pool's cash stands after this line. */
  balanceCents: number;
  /** This award (takes the pool below zero) and the top-up that goes with this approval. */
  tone: 'award' | 'top-up' | null;
}

export interface OverageLedger {
  /** The year's cash budget: every ledger entry so far. */
  budgetCents: number;
  /** Paid and in progress this year, before this award. */
  awardedCents: number;
  rows: OverageLedgerRow[];
}

const ENTRY_WORDS: Readonly<Record<PoolLedger['entries'][number]['kind'], string>> = {
  initial: 'budget',
  addition: 'Addition',
  reduction: 'Reduction',
  move_in: 'Move',
  move_out: 'Move',
  top_up: 'Top-up for over-budget',
};

/**
 * X2's ledger for one over-budget pool: its budget entries this year, the awards so far, this award,
 * and the top-up as drafted. "Awarded so far" comes from the approval's own numbers (the budget
 * less what was left before this award), so the rows always add up to the tiles above them.
 * Deleted entries are left out: Budget pools shows them.
 */
export function overageLedger(input: {
  ledger: PoolLedger;
  detail: ApprovalDetail;
  draft: TopUpDraft;
  /** Who is approving: the top-up goes in the ledger in their name. */
  approver: string;
  /** The organization's today (YYYY-MM-DD). */
  today: string;
}): OverageLedger {
  const { ledger, detail, draft, approver, today } = input;
  const { award } = detail;
  const name = draft.pool.name;
  const live = [...ledger.entries]
    .filter((e) => !e.voided)
    .sort((a, b) => a.at.localeCompare(b.at));
  const rows: OverageLedgerRow[] = [];
  let running = 0;
  for (const e of live) {
    running += e.cashCents;
    const entry =
      e.kind === 'initial'
        ? `${String(ledger.fiscalYear)} budget`
        : e.kind === 'move_in' && e.otherPool !== null
          ? `Move from ${e.otherPool.name}`
          : e.kind === 'move_out' && e.otherPool !== null
            ? `Move to ${e.otherPool.name}`
            : e.kind === 'top_up' && e.award?.displayId != null
              ? `${ENTRY_WORDS.top_up} · ${e.award.displayId}`
              : ENTRY_WORDS[e.kind];
    rows.push({
      id: e.id,
      date: formatDate(e.at),
      entry,
      sub: [
        e.kind === 'initial'
          ? `Set by ${e.by?.name ?? 'MyRewards setup'}`
          : `By ${e.by?.name ?? 'MyRewards setup'}`,
        e.reason,
      ]
        .filter((part): part is string => part !== null && part.trim() !== '')
        .join(' · '),
      amountCents: e.cashCents,
      balanceCents: running,
      tone: null,
    });
  }
  const budgetCents = ledger.position.budget?.cashCents ?? running;
  const demand = demandOf(detail, draft.pool.id);
  const before = draft.remainingAfter.cashCents + demand.cashCents;
  const awardedCents = budgetCents - before;
  const people = detail.lines.filter((l) => l.included).length;
  rows.push(
    {
      id: 'awarded',
      date: `Through ${formatShortDate(today)}`,
      entry: 'Awards paid and in progress',
      sub: `${name} awards so far this year`,
      amountCents: -awardedCents,
      balanceCents: before,
      tone: null,
    },
    {
      id: award.id,
      date: formatDate(award.submittedAt ?? today),
      entry: award.displayId ?? 'This award',
      sub: [
        award.program?.name ?? award.title,
        plural(people, 'person', 'people'),
        award.createdBy.name,
      ]
        .filter((part): part is string => part !== null && part !== '')
        .join(' · '),
      amountCents: -demand.cashCents,
      balanceCents: draft.remainingAfter.cashCents,
      tone: 'award',
    },
  );
  const top = draft.cashCents ?? 0;
  if (top > 0)
    rows.push({
      id: 'top-up',
      date: formatDate(today),
      entry: `Top-up by ${approver}`,
      sub: 'Goes into the budget ledger as Top-up for over-budget',
      amountCents: top,
      balanceCents: afterTopUp(draft).cashCents,
      tone: 'top-up',
    });
  return { budgetCents, awardedCents, rows };
}

// --- A pool in the hero (SL7) ----------------------------------------------------------------------------

/**
 * A pool as the hero's stat, scoped to its year: "Maritime Airborne · cash left 2026 · $160,405 ·
 * of $335,905 · options left 7,956". `left` is null while the year's budget isn't set; `budgetCents`
 * is null until the budgets have loaded.
 */
export function poolStat(pool: {
  name: string;
  fiscalYear: number;
  left: Units | null;
  budgetCents: number | null;
}): { label: ScopedLabel; value: string; meta: string; tone?: 'muted' } {
  const label = scoped(pool.name, `cash left ${String(pool.fiscalYear)}`);
  if (pool.left === null)
    return {
      label,
      value: 'Not set',
      tone: 'muted',
      meta: 'An Executive sets this pool’s budget in Budget pools',
    };
  const awarded = pool.budgetCents === null ? null : pool.budgetCents - pool.left.cashCents;
  return {
    label,
    value:
      pool.left.cashCents < 0
        ? `${formatMoney(-pool.left.cashCents)} over budget`
        : formatMoney(pool.left.cashCents),
    meta: [
      pool.budgetCents === null ? null : `of ${formatMoney(pool.budgetCents)}`,
      awarded === 0 ? '$0 awarded' : null,
      `options left ${formatCount(pool.left.options)}`,
    ]
      .filter((part): part is string => part !== null)
      .join(' · '),
  };
}

// --- The list ----------------------------------------------------------------------------------------

/**
 * Whether the reader decides this in their own right (APP-148): a Stock Admin's list also holds
 * what they may only do for an Executive or a Sector Lead (`actingFor`), which is Waiting on
 * others, never "waiting on you". Theirs is a decision they take themselves: approve, decline,
 * return, reject or record it. A Stock Admin may move a TAA's people in their own right, which
 * doesn't make the decision theirs. An all-conflicted award an Executive may see but not decide
 * (APP-121) stays theirs.
 */
export function decidesOwn(detail: ApprovalDetail): boolean {
  const { actions } = detail;
  return (
    detail.actingFor == null ||
    actions.approve ||
    actions.decline ||
    actions.return ||
    actions.reject ||
    actions.recordDecision ||
    detail.approvalAvailability === 'no_independent_executive'
  );
}

export const KIND_ORDER: readonly ApprovalKind[] = [
  'self_recognition',
  'delegate',
  'overage',
  'executive',
  'record_decision',
];

/** One card in the list: the award, what it asks for, and who entered it. */
export function listCard(detail: ApprovalDetail): {
  id: string;
  displayId: string;
  summary: string;
  meta: string;
} {
  const { award } = detail;
  const included = detail.lines.filter((l) => l.included);
  const cash = included.reduce((s, l) => s + l.cashCents, 0);
  const options = included.reduce((s, l) => s + l.options, 0);
  const by = award.onBehalfOf
    ? `${award.createdBy.name} for ${award.onBehalfOf.name}`
    : award.createdBy.name;
  return {
    id: award.id,
    displayId: award.displayId ?? 'New award',
    summary: `${TYPE_LABEL[award.type]} · ${plural(included.length, 'person', 'people')} · ${amountWords(cash, options)}`,
    meta: `Entered by ${by}${award.program ? ` · ${award.program.name}` : ''}`,
  };
}

/** "3 people × $1,500" when everyone gets the same; else the total. */
export function peopleSummary(detail: ApprovalDetail): string {
  const included = detail.lines.filter((l) => l.included);
  const first = included[0];
  if (
    first &&
    included.every((l) => l.cashCents === first.cashCents && l.options === first.options)
  )
    return `${plural(included.length, 'person', 'people')} × ${amountWords(first.cashCents, first.options)}`;
  const cash = included.reduce((s, l) => s + l.cashCents, 0);
  const options = included.reduce((s, l) => s + l.options, 0);
  return `${plural(included.length, 'person', 'people')} · ${amountWords(cash, options)}`;
}

// --- Nothing waiting (QA-010) ---------------------------------------------------------------------

export interface EmptyApprovalSection {
  key: 'delegate' | 'executive' | 'requester' | 'other';
  title: string;
  headline: string;
  body: string;
  art: 'people' | 'pool' | 'award' | 'calendar';
  action: { label: string; href: string } | null;
  /** This queue has had work and it's all done: "All caught up" (DESIGN-SPEC §5, APP-070). */
  caughtUp: boolean;
}

/** X3.empty: an Executive hears the PAA/TAA budget isn't set, and that they can set it at approval. */
export function paaUnsetWords(year: number): string {
  return `The ${String(year)} PAA/TAA budget isn’t set yet: set it in Budget pools, or when you approve the first PAA or TAA.`;
}

/** "Sam Ortega", "Sam Ortega and Farah Haddad", "Sam Ortega, Farah Haddad and Ben Adeyemi". */
export function names(list: readonly string[]): string {
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1] ?? ''}`;
}

/**
 * APP-204: who reads a decline's note, said where it's written, when one of them doesn't see the
 * budget (a delegate, APP-112): "Sam Ortega and Graham Whitlock read this note. Sam doesn't see
 * budgets, so leave the budget out of it." Nothing to say when everyone who reads it sees it.
 */
export function declineNoteHint(detail: ApprovalDetail): string | undefined {
  const readers = detail.declineNoteReaders ?? [];
  const kept = readers.filter((r) => !r.seesBudget);
  if (kept.length === 0) return undefined;
  const who = names(readers.map((r) => r.name));
  const keptWho = names(kept.map((r) => firstName(r.name)));
  return `${who} ${readers.length === 1 ? 'reads' : 'read'} this note. ${keptWho} ${kept.length === 1 ? 'doesn’t' : 'don’t'} see budgets, so leave the budget out of it.`;
}

/**
 * Since APP-106, a Sector bonus from someone a System Admin named to start one, who doesn't lead
 * the sector or enter for its lead, stops for the Sector Lead's OK too (APP-111 §8). The Lead isn't
 * shown who is named, so the empty page says it without names.
 */
const NAMED_STARTERS =
  'A Sector bonus entered by someone a System Admin named to start one in your sector stops here too.';

/**
 * What lands on Approvals for each role this person holds, said to that role (SL7.empty, R3.empty,
 * X2/X3): a Sector Lead hears about their delegates, by name when the sector setup is open to them,
 * and about people named to start Sector bonuses there (never by name);
 * a requester about recording the Executives' decision; an Executive about overages and the
 * Board's PAA and TAA decisions. A queue that has had work and is all done says "All caught up";
 * one that never has, what will land there. Without `history` (an older server), first-day words:
 * never a "caught up" it can't back (APP-070, APP-067 §9).
 */
export function emptyApprovals(
  roles: readonly RoleKey[],
  delegates: readonly { name: string; needsApproval: boolean; active: boolean }[] | null,
  history: ApprovalHistory | null = null,
  /** The year whose PAA/TAA budget isn't set, for an Executive (X3.empty); null when it is. */
  paaUnsetYear: number | null = null,
): { title: string; sub: string; sections: EmptyApprovalSection[] } {
  const sections: EmptyApprovalSection[] = [];
  if (roles.includes('sector_lead')) {
    const active = (delegates ?? []).filter((d) => d.active);
    const stop = active.filter((d) => d.needsApproval).map((d) => d.name);
    const through = active.filter((d) => !d.needsApproval).map((d) => d.name);
    const none = delegates !== null && active.length === 0;
    const who =
      delegates === null
        ? `Entries from a delegate who needs your OK stop here first. ${NAMED_STARTERS}`
        : none
          ? `You have no delegates yet. Add one in Sector settings, and choose whether their entries need your OK. ${NAMED_STARTERS}`
          : [
              stop.length > 0
                ? `${names(stop)}’s entries stop here for your OK.`
                : 'None of your delegates needs your OK, so their entries go straight through.',
              stop.length > 0 && through.length > 0
                ? `${names(through)}’s go straight through.`
                : null,
              NAMED_STARTERS,
              'Either way, we email you the moment one is entered.',
            ]
              .filter((part): part is string => part !== null)
              .join(' ');
    const caughtUp = history?.delegate === true;
    // SL7.empty's words (APP-093). With no delegates at all, the headline says that instead.
    sections.push({
      key: 'delegate',
      title: 'Entries for your OK',
      headline: caughtUp
        ? 'All caught up'
        : none
          ? 'No delegates yet'
          : 'Nothing entered for your OK yet',
      body: caughtUp
        ? `Every entry sent for your OK has been decided. ${who}`
        : none
          ? `Add one in Sector settings, and choose whether their entries need your OK. Those that do stop here first. ${NAMED_STARTERS} We email you the moment one is entered.`
          : who,
      art: 'people',
      action: { label: 'Manage delegates', href: '/settings/sector' },
      caughtUp,
    });
  }
  if (roles.includes('executive')) {
    const caughtUp = history?.executive === true;
    sections.push({
      key: 'executive',
      title: 'Executive decisions',
      headline: caughtUp ? 'All caught up' : 'No decisions waiting',
      body: [
        caughtUp
          ? 'Every overage and every PAA and TAA sent to the Executives has been decided. The next one lands here for any one Executive, and we email you all.'
          : 'An award that would take a pool below zero waits here for any one Executive, with the top-up that brings the pool back to zero. So do the PAAs and TAAs the Board decides.',
        'Entries from a delegate who needs your OK stop here too.',
        paaUnsetYear === null ? null : paaUnsetWords(paaUnsetYear),
      ]
        .filter((part): part is string => part !== null)
        .join(' '),
      art: 'pool',
      action: { label: 'Budget pools', href: '/manage/executive/budget-pools' },
      caughtUp,
    });
  }
  if (roles.includes('requester') || roles.includes('executive_delegate')) {
    const caughtUp = history?.requester === true;
    sections.push({
      key: 'requester',
      title: 'Decisions to record',
      headline: caughtUp ? 'All caught up' : 'No decision to record yet',
      body: caughtUp
        ? 'Nothing of yours is waiting to be recorded. When the Executives decide your next request, you record their decision here with the proof.'
        : 'When the Executives decide an Executive Discretionary request outside the app, you record their decision here with the proof, and it goes on to the Stock Admin team.',
      art: 'award',
      action: { label: 'Start a request', href: '/recognize' },
      caughtUp,
    });
  }
  if (sections.length === 0)
    sections.push({
      key: 'other',
      title: 'Approvals',
      headline: 'Nothing waiting on you',
      body: 'Approvals land here when one of your roles has a decision to make. We email you when one arrives.',
      art: 'calendar',
      action: { label: 'Back to Home', href: '/' },
      caughtUp: false,
    });
  const [first] = sections;
  // R3.empty: a requester and nothing else hears what this page is for.
  const requesterOnly = sections.length === 1 && first?.key === 'requester';
  const title = requesterOnly ? 'Record what the Executives approved' : 'Nothing waiting on you';
  const sub =
    requesterOnly && !first.caughtUp
      ? 'Nothing is waiting to be recorded. When the Executives decide on one of your requests, you’ll update it here and attach their approval.'
      : sections.every((section) => section.caughtUp)
        ? 'All caught up. We email you the moment a new decision lands here.'
        : sections.length === 1 && first?.key === 'delegate'
          ? 'When a delegate who needs your OK enters an award, it stops here first, and so does a Sector bonus entered by someone a System Admin named to start one in your sector.'
          : sections.length === 1 && first?.key === 'executive'
            ? 'Overages and the Board’s PAA and TAA decisions wait here for any one Executive.'
            : 'Decisions for each of your roles land here, oldest first. We email you when one arrives.';
  return { title, sub, sections };
}

// --- Who's been recognized lately (QA-006) -------------------------------------------------------

type Recent = NonNullable<Line['recent']>;
type Rule = NonNullable<ApprovalDetail['repeatRule']>;

/** "2 awards · last Sep 30, $1,500", or "None in 12 months". */
export function recentWords(recent: Recent, rule: Rule): string {
  if (recent.awards === 0 || recent.last === null)
    return `None in ${plural(rule.months, 'month', 'months')}`;
  return `${plural(recent.awards, 'award', 'awards')} · last ${formatShortDate(recent.last.date)}, ${formatMoney(recent.last.valueCents)}`;
}

/** "Repeat" when they already are one, "Would be Repeat" when this award would make them one. */
export function repeatPill(recent: Recent): string | null {
  if (recent.repeat) return 'Repeat';
  if (recent.wouldBeRepeat) return 'Would be Repeat';
  return null;
}

/** "Repeat = 3+ awards or $5,000+ in 12 months": the setting in force (Q18: a setting, default 3 / $5,000 / 12). */
export function repeatRuleWords(rule: Rule): string {
  return `Repeat = ${String(rule.awards)}+ awards or ${formatMoney(rule.valueCents)}+ in ${plural(rule.months, 'month', 'months')}`;
}

/** The line was changed while it waited: its amounts differ from what was sent. */
export function changedSinceSent(line: Line): boolean {
  return (
    line.requested.cashCents !== line.cashCents ||
    line.requested.options !== line.options ||
    line.requested.totalValueCents !== line.totalValueCents
  );
}

/** "Sent as $1,000 value" (an older PAA, APP-151), "Sent as $1,500 + 100 options". */
export function sentWords(line: Line, byValue: boolean): string {
  if (byValue && line.requested.totalValueCents !== null)
    return `Sent as ${formatMoney(line.requested.totalValueCents)} value`;
  return `Sent as ${amountWords(line.requested.cashCents, line.requested.options)}`;
}

/** "Waiting since today", "Waiting 3 days": how long it has waited for a decision. */
export function waitingWords(queuedAt: string, asOf: string): string {
  const days = daysBetween(organizationDate(queuedAt), asOf);
  return days <= 0 ? 'Waiting since today' : `Waiting ${plural(days, 'day', 'days')}`;
}
